import React from "react";

import { Collapse, Paper, Stack, useTheme } from "@mui/material";

import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";
import type { BuildLogJob } from "src/domains/application/module/api/IApplicationApi";

import { filterStepEvents } from "./job-detail-helpers";
import { JobDetailHeader } from "./JobDetailHeader";
import { JobDetailLogs } from "./JobDetailLogs";
import { JobDetailMetadata } from "./JobDetailMetadata";

// ── Main Panel ────────────────────────────────────────────────────────────────

export interface JobDetailPanelProps {
  open: boolean;
  onClose: () => void;
  nodeId: string | null;
  nodeData: {
    label: string;
    step: string;
    nodeType: string;
    status: string;
    message: string;
    serviceName?: string;
    serviceType?: string;
    startedAt?: string | null;
    isStale: boolean;
    /** Services within a group node — populated for group-build/group-deploy */
    services?: Array<{
      name: string;
      type: string;
      status: string;
      message: string;
    }>;
  } | null;
  events: DeploymentEvent[];
  buildLogJobs?: BuildLogJob[];
  /** Live log lines from rusha_managed builds, keyed by service → phase → lines */
  liveLogs?: Map<string, Map<string, string[]>>;
  deployMode?: string;
  /** CI quality gate jobs from the provisioned ci.yml (rusha_managed only) */
  qualityGates?: BuildLogJob[];
  templateServices?: { name: string; type: string }[];
  buildFailed?: boolean;
  retryLoading?: boolean;
  onRetryBuild?: () => void;
  forceFailLoading?: boolean;
  onForceFail?: () => void;
  /**
   * Two-level navigation for group nodes:
   * null  = show service list
   * "svc" = show that service's events + log
   */
  selectedPanelService?: string | null;
  onServiceSelect?: (service: string | null) => void;
  // Legacy chip-selector props (kept for backward compat, unused in new design)
  buildGroupServices?: string[];
  selectedGroupService?: string;
  onGroupServiceChange?: (service: string) => void;
  /** Pipeline run metadata — populated for rusha_managed builds */
  pipelineRunId?: number | string;
  pipelineRunStatus?: string;
  pipelineRunConclusion?: string | null;
  pipelineRunHtmlUrl?: string;
}

export function JobDetailPanel({
  open,
  onClose,
  nodeData,
  events,
  buildLogJobs,
  liveLogs,
  deployMode: _deployMode,
  qualityGates,
  templateServices,
  buildFailed,
  retryLoading,
  onRetryBuild,
  forceFailLoading,
  onForceFail,
  selectedPanelService,
  onServiceSelect,
  pipelineRunId,
  pipelineRunStatus,
  pipelineRunConclusion,
  pipelineRunHtmlUrl,
}: JobDetailPanelProps): React.ReactElement {
  const theme = useTheme();

  if (!nodeData) return <></>;

  const { label, step, nodeType, status, startedAt, isStale, services } =
    nodeData;

  const isGroupNode =
    nodeType === "group-build" ||
    nodeType === "group-deploy" ||
    nodeType === "group-ci";

  // Level 2: a service is selected inside a group → show its events + log
  const activeSvc: string | null = isGroupNode
    ? (selectedPanelService ?? null)
    : null;

  // Events filtered to the active service (level 2) or step + service for non-group nodes
  const stepEvents = filterStepEvents(
    events,
    step,
    isGroupNode,
    activeSvc,
    nodeData.serviceName,
  );

  const isInProgress = status === "in_progress";

  const _borderColor =
    status === "failed"
      ? theme.palette.error.main
      : isInProgress
        ? theme.palette.info.main
        : status === "completed"
          ? theme.palette.success.main
          : theme.palette.divider;

  // Title depends on the navigation level
  const title = activeSvc
    ? `${activeSvc} · ${step === "build" ? "Build" : "Deploy"}`
    : label;

  // Status for the active service row (when drilled in)
  const activeSvcData = activeSvc
    ? services?.find((s) => s.name === activeSvc)
    : null;
  const displayStatus = activeSvcData?.status ?? status;

  const displayBorderColor =
    displayStatus === "failed"
      ? theme.palette.error.main
      : displayStatus === "in_progress"
        ? theme.palette.info.main
        : displayStatus === "completed"
          ? theme.palette.success.main
          : theme.palette.divider;

  return (
    <Collapse in={open} timeout={300}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${displayBorderColor}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack sx={{ overflow: "hidden" }}>
          <JobDetailHeader
            activeSvc={activeSvc}
            onServiceSelect={onServiceSelect}
            step={step}
            title={title}
            displayStatus={displayStatus}
            startedAt={startedAt}
            isStale={isStale}
            displayBorderColor={displayBorderColor}
            onClose={onClose}
            isGroupNode={isGroupNode}
            isInProgress={isInProgress}
            buildFailed={buildFailed}
            retryLoading={retryLoading}
            onRetryBuild={onRetryBuild}
            forceFailLoading={forceFailLoading}
            onForceFail={onForceFail}
            buildLogJobs={buildLogJobs}
          />

          <JobDetailMetadata
            step={step}
            pipelineRunId={pipelineRunId}
            pipelineRunStatus={pipelineRunStatus}
            pipelineRunConclusion={pipelineRunConclusion}
            pipelineRunHtmlUrl={pipelineRunHtmlUrl}
            isGroupNode={isGroupNode}
            activeSvc={activeSvc}
            services={services}
            onServiceSelect={onServiceSelect}
          />

          <JobDetailLogs
            isGroupNode={isGroupNode}
            activeSvc={activeSvc}
            serviceName={nodeData.serviceName}
            step={step}
            stepEvents={stepEvents}
            liveLogs={liveLogs}
            buildLogJobs={buildLogJobs}
            qualityGates={qualityGates}
            templateServices={templateServices}
          />
        </Stack>
      </Paper>
    </Collapse>
  );
}
